import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Trophy, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Confetti } from '@/components/ui/confetti';
import { Prize } from '@/hooks/usePrizes';

interface PrizeWinnerNotificationProps {
  prize: Prize | null;
  onClose: () => void;
  onClaim: () => void;
  formatMonthYear: (monthYear: string) => string;
}

export function PrizeWinnerNotification({
  prize,
  onClose,
  onClaim,
  formatMonthYear,
}: PrizeWinnerNotificationProps) {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (prize) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [prize]);

  if (!prize) return null;

  return (
    <>
      <Confetti isActive={showConfetti} />
      
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 max-w-sm w-full border border-amber-500/30 shadow-2xl shadow-amber-500/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-green-500/10 rounded-2xl" />
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative z-10 text-center">
              {/* Trophy animation */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", bounce: 0.6, delay: 0.2 }}
                className="inline-block mb-4"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-amber-500/30 blur-2xl rounded-full animate-pulse" />
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-500/30 border-2 border-amber-400/50 rounded-full p-5 relative">
                    <Trophy className="h-12 w-12 text-amber-400" />
                  </div>
                  {/* Sparkles */}
                  <motion.div
                    animate={{ 
                      rotate: 360,
                      scale: [1, 1.2, 1],
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute -top-2 -right-2"
                  >
                    <Sparkles className="h-6 w-6 text-yellow-400" />
                  </motion.div>
                </div>
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-white mb-2"
              >
                🎉 ¡Felicidades!
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-amber-300 font-semibold text-lg mb-4"
              >
                ¡Ganaste el premio del mes!
              </motion.p>

              {/* Prize details */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-black/30 rounded-xl p-4 mb-4 border border-white/10"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Gift className="h-5 w-5 text-green-400" />
                  <span className="font-bold text-white">{prize.prize_name}</span>
                </div>
                <p className="text-sm text-zinc-400 capitalize">
                  {formatMonthYear(prize.month_year)}
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-3"
              >
                <Button
                  onClick={onClaim}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold shadow-lg shadow-green-500/20"
                >
                  <Gift className="h-4 w-4 mr-2" />
                  Reclamar Premio
                </Button>
                <button
                  onClick={onClose}
                  className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  Ver después
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}
